import React, { useRef, useEffect, useState } from 'react';
import { useChat } from 'store/useChatStore';
import MessageList from './message-list';
import Suggestions from './suggestions';
import ChatInput from './chat-input';
import LoadingIndicator from './loadingIndicator';
import DataExplorer from './data-explorer';
import useConversations from 'store/useConversationStore';
import { RootState } from 'redux/store';
import { useAppSelector } from 'redux/hooks';
import { RotatingLogo } from 'components/atoms/Base/Logo/RotatingLogo';

const ChatContainer: React.FC<{ sidebarCollapsed: boolean }> = ({ sidebarCollapsed }) => {
  const { 
    messages, 
    pendingMessage, 
    sendChatMessage, 
    suggestions,
    suggestedVisualizations,
    sendSuggestion,
    loading
  } = useChat();
  const { closeDataExplorer } = useConversations();
  const { dataExplorer } = useAppSelector((state: RootState) => state.conversations);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const [isViewSchema, setIsViewSchema] = useState<boolean>(false);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const handleVisualizationClick = (type: string, description: string) => {
    if (description.includes(type)) {
      sendChatMessage(`Show me a ${description}`);
    } else {
      sendChatMessage(`Show me a ${description} in a ${type}`);
    }
  };

  const handleCloseDrawer = () => {
    closeDataExplorer();
  };

  useEffect(() => {
    setIsViewSchema(dataExplorer);
  }, [dataExplorer]);

  return (
    <div 
      className={`
        w-full h-screen bg-white flex flex-col overflow-hidden
        md:ml-[260px] md:w-[calc(100%-260px)]
        ${sidebarCollapsed ? 'md:ml-[60px] md:w-[calc(100%-60px)]' : ''}
      `}
    >
      <div className="w-full h-full flex flex-col">
        {messages.length === 0 ? (
          loading ? (
            <div className="flex justify-start z-10 items-center p-3 w-full max-w-[856px] mx-auto">
              <LoadingIndicator />
            </div>
          ) : (
            <div className="flex-1 flex flex-col items-center justify-center px-8 py-8 overflow-y-auto">
              {/* Logo */}
              <div className="flex justify-center">
                <RotatingLogo
                  size={150}
                  rpm={30}
                />
              </div>

              {/* Title + Subtitle */}
              <div className="text-center w-auto h-auto mt-4">
                <h1 className="md:text-display text-[24px] text-gray-900 mb-4">
                  Your{' '}
                  <span className="bg-gradient-to-r from-cyan-500 to-green-500 bg-clip-text text-transparent">
                    AI Analyst
                  </span>
                  , on demand
                </h1>
                <p className="font-inter text-[20px] md:text-[24px] leading-[28px] md:leading-[32px] text-tertiary-text">
                  Get instant answers from your data.
                </p>
              </div>

              {/* Chat input - aligned with message width */}
              <div className="mt-8 w-full max-w-[856px] px-4 md:px-8">
                <ChatInput
                  onSendMessage={sendChatMessage}
                  isLoading={pendingMessage}
                  disabled={pendingMessage}
                />
              </div>
            </div>
          )
        ) : (
          <>
            {/* Scrollable messages */}
            <div className="flex-1 overflow-y-auto [&::-webkit-scrollbar]:w-[5px] [&::-webkit-scrollbar-track]:bg-gray-100 [&::-webkit-scrollbar-thumb]:bg-gray-300 [&::-webkit-scrollbar-thumb]:rounded-sm [&::-webkit-scrollbar-thumb:hover]:bg-gray-400">
              <div className="w-full max-w-[856px] mx-auto px-4 py-4 md:px-8 md:py-8">
                <MessageList messages={messages} />
                <div ref={messagesEndRef} />

                {(suggestions.length > 0 ||
                  (suggestedVisualizations && suggestedVisualizations.length > 0)) &&
                  !pendingMessage && (
                    <div className="mt-4">
                      <Suggestions
                        suggestions={suggestions}
                        suggestedVisualizations={suggestedVisualizations}
                        onSuggestionClick={sendSuggestion}
                        onVisualizationClick={handleVisualizationClick}
                      />
                    </div>
                  )}
              </div>
            </div>

            {/* Chat input fixed bottom - aligned with messages */}
            <div className="flex-shrink-0  bg-white">
              <div className="w-full max-w-[856px] mx-auto px-4 py-4 md:px-8 md:py-6">
                <ChatInput
                  onSendMessage={sendChatMessage}
                  isLoading={pendingMessage}
                  disabled={pendingMessage}
                />
              </div>
            </div>
          </>
        )}

        <DataExplorer isOpen={isViewSchema} onClose={handleCloseDrawer} />
      </div>
    </div>
  );
};

export default ChatContainer;